import { fromJS } from 'immutable';
import { isDevelopment } from 'flow/utils/app-utils';

// --- Mock participants (dev / layout testing only) ------------------------
//
// Flip ENABLE_MOCK_PARTICIPANTS to true locally to drive a meeting with a fixed
// set of MOCK_PARTICIPANT_COUNT simulated camera-on participants — handy for
// eyeballing the layout engine and the active-speaker ordering/highlight. The
// mock set is injected into the real stores (via useMockParticipants →
// setActiveMeetingMembers + setVideoNewMeetingVideoMemberAction), so it flows
// through useMemberManagement, pin pruning and the layout engine on the exact
// same path as real participants — no mock-aware branching in the render tree.
// The MockSpeakerToggleOverlay lets you toggle which mocks are "speaking" to
// exercise the active-speaker layout. Tiles are flat-coloured (MOCK_COLORS) so
// the packing is easy to read. Must stay false on commit.

const ENABLE_MOCK_PARTICIPANTS: boolean = false;

// ANDed with isDevelopment() so even a stray `true` committed above can never
// render mock participants in a non-dev build.
export const MOCK_PARTICIPANTS_ENABLED =
    ENABLE_MOCK_PARTICIPANTS && isDevelopment();

export const MOCK_MEMBER_NAMES = [
    'Alice',
    'Bob',
    'Charlie',
    'Diana',
    'Eve',
    'Frank',
    'Grace',
    'Hank',
    'Ivy',
    'Jack',
    'Karen',
    'Leo',
];

/** Fixed number of simulated participants to inject. */
export const MOCK_PARTICIPANT_COUNT = 10;

/** The names actually used, given MOCK_PARTICIPANT_COUNT. */
export const MOCK_MEMBER_NAMES_IN_USE = MOCK_MEMBER_NAMES.slice(
    0,
    MOCK_PARTICIPANT_COUNT,
);

/** Per-name flat tile colour, so each mock tile is visually distinct. */
export const MOCK_COLORS: Record<string, string> = {
    Alice: '#2563eb',
    Bob: '#16a34a',
    Charlie: '#d97706',
    Diana: '#9333ea',
    Eve: '#dc2626',
    Frank: '#0891b2',
    Grace: '#c026d3',
    Hank: '#4f46e5',
    Ivy: '#059669',
    Jack: '#ea580c',
    Karen: '#7c3aed',
    Leo: '#0d9488',
};

/**
 * Build `count` mock conference members shaped like the real meeting-member
 * list — an Immutable List of member maps, as `setActiveMeetingMembers`
 * expects. They carry no videoMember, so they resolve to dial-in participant
 * tiles (avatar + name), flat-coloured via {@link MOCK_COLORS}.
 */
export const buildMockMeetingMembers = (count: number) =>
    fromJS(
        MOCK_MEMBER_NAMES.slice(0, count).map((name, index) => ({
            displayName: name,
            key: `mock-${index}`,
            number: `100${index}`,
            joinTime: index,
        })),
    );
